import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/shared/components/Buttons';
import { ButtonVariant } from '@/shared/components/Buttons/types';
import InlineLayout from '@/shared/layout/inlineLayout';
import Loader from '@/app/routes/Loader';
import { Snackbar } from '@/shared/components/Snackbar';
import { useViewProfile } from '../../hooks/useViewProfile';
import ProfileBasicInfo from './ProfileBasicInfo';
import ProfilePermissions from './ProfilePermissions';
import UpdatePasswordForm from './UpdatePasswordForm';

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [successMessage] = React.useState(
    () =>
      (location.state as { successMessage?: string } | null)?.successMessage ??
      ''
  );
  const [successSnackbarOpen, setSuccessSnackbarOpen] = React.useState(() =>
    Boolean(successMessage)
  );
  const {
    profileData,
    isLoading,
    showErrorScreen,
    name,
    initials,
    profileImageUrl,
    statusLabel,
    statusBadgeClasses,
    roleLabel,
    isClient,
    isTechnician,
    navItems,
  } = useViewProfile();

  const [imgError, setImgError] = React.useState(false);

  // Reset error state when URL changes (e.g. after refetch)
  React.useEffect(() => {
    setImgError(false);
  }, [profileImageUrl]);

  const profileHeader = (
    <>
      {/* Avatar */}
      <div className="flex items-center gap-4">
        <div
          className="flex items-center justify-center shrink-0 rounded-full bg-[#E8EEF0] text-[#223746] font-medium overflow-hidden"
          style={{
            width: '64px',
            height: '64px',
            fontSize: '22px',
            lineHeight: '32px',
          }}
        >
          {profileImageUrl && !imgError ? (
            <img
              src={profileImageUrl}
              alt=""
              className="w-full h-full object-cover"
              onError={() => setImgError(true)}
            />
          ) : (
            initials
          )}
        </div>
      </div>
      <div className="flex min-w-0 flex-1">
        <div className="flex min-w-0 flex-col gap-[4px]">
          <h2
            className="min-w-0 break-all font-medium text-neutral-10"
            style={{ fontSize: '18px', lineHeight: '28px' }}
          >
            {name}
          </h2>
          <span
            className="px-2 py-1 rounded-[8px] text-sm font-medium bg-information-90 text-information-30 w-fit"
            style={{ fontSize: '14px', lineHeight: '20px' }}
          >
            {roleLabel}
          </span>
        </div>
      </div>

      {/* Status chip + Edit Profile button */}
      <div className="flex items-center gap-3 shrink-0">
        <span
          className={`px-3 py-1 rounded-[8px] text-sm font-medium flex items-center gap-2 ${statusBadgeClasses.container}`}
          style={{ fontSize: '14px', lineHeight: '20px' }}
        >
          <div
            className={`rounded-full shrink-0 ${statusBadgeClasses.dot}`}
            style={{ width: '6px', height: '6px' }}
          />
          {statusLabel}
        </span>
        <Button
          label="Edytuj profil"
          variant={ButtonVariant.Primary}
          size="Medium"
          onClick={() => navigate('/Profil/Edytuj-profil')}
          className="!py-[10px] !px-[16px] !font-medium !text-[14px] hover:cursor-pointer"
          style={{ borderRadius: '8px' }}
        />
      </div>
    </>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader />
      </div>
    );
  }

  if (showErrorScreen) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-on-surface-variant">
          Nie udało się załadować profilu. Spróbuj ponownie później.
        </p>
      </div>
    );
  }

  const contentByNavId: Record<string, React.ReactNode> = {
    basic: <ProfileBasicInfo profileData={profileData} />,
    password: <UpdatePasswordForm />,
    ...(isClient
      ? {
          permissions: (
            <ProfilePermissions
              assignedPermissions={profileData?.assignedPermissions ?? null}
            />
          ),
        }
      : {}),
    ...(isTechnician
      ? {
          permissions: (
            <ProfilePermissions
              technicianPrivileges={profileData?.privileges ?? null}
            />
          ),
        }
      : {}),
  };

  return (
    <>
      <InlineLayout
        title="Profil"
        navItems={[...navItems]}
        contentByNavId={contentByNavId}
        header={profileHeader}
        searchParamKey="tab"
        defaultActiveId="basic"
      />
      <Snackbar
        open={successSnackbarOpen}
        message={successMessage}
        variant="success"
        onClose={() => setSuccessSnackbarOpen(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
    </>
  );
};

export default ProfilePage;
